/**
 * FeatureGate - Conditionally renders content based on plan features
 *
 * Usage:
 *   <FeatureGate feature="dbt_enabled">
 *     <DbtIntegrationPanel />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="lineage_enabled" fallback={<UpgradePrompt />}>
 *     <LineageViewer />
 *   </FeatureGate>
 */

import React from 'react';
import { useBilling } from '../../contexts/BillingContext';
import UpgradePrompt from './UpgradePrompt';

const FeatureGate = ({
  feature,
  children,
  fallback = null,
  showUpgradePrompt = true,
  upgradeMessage = null,
}) => {
  const { isFeatureEnabled, loading, planTier } = useBilling();

  // While loading, show nothing or a skeleton
  if (loading) {
    return null;
  }

  // Check if the feature is enabled
  const isEnabled = isFeatureEnabled(feature);

  if (isEnabled) {
    return <>{children}</>;
  }

  // Feature not enabled - show fallback or upgrade prompt
  if (fallback) {
    return <>{fallback}</>;
  }

  if (showUpgradePrompt) {
    return (
      <UpgradePrompt
        feature={feature}
        currentPlan={planTier}
        message={upgradeMessage}
      />
    );
  }

  return null;
};

/**
 * LimitGate - Conditionally renders content based on plan limits
 *
 * Usage:
 *   <LimitGate limit="connections" action="create">
 *     <CreateConnectionButton />
 *   </LimitGate>
 */
export const LimitGate = ({
  limit,
  children,
  fallback = null,
  showUpgradePrompt = true,
  upgradeMessage = null,
}) => {
  const { canPerformAction, getLimitStatus, loading, planTier } = useBilling();

  if (loading) {
    return null;
  }

  const canProceed = canPerformAction(limit);
  const status = getLimitStatus(limit);

  if (canProceed) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (showUpgradePrompt) {
    return (
      <UpgradePrompt
        limit={limit}
        currentPlan={planTier}
        currentUsage={status.current}
        maxUsage={status.max}
        message={upgradeMessage}
      />
    );
  }

  return null;
};

/**
 * TierGate - Shows content only for specific plan tiers
 *
 * Usage:
 *   <TierGate minTier="growth">
 *     <AdvancedFeatures />
 *   </TierGate>
 */
export const TierGate = ({
  minTier,
  allowedTiers = null,
  children,
  fallback = null,
  showUpgradePrompt = true,
}) => {
  const { plan, planTier, loading } = useBilling();

  if (loading) {
    return null;
  }

  // Plan unknown (load failed): allow — backend gates real mutations.
  if (!plan) {
    return <>{children}</>;
  }

  const tierOrder = ['free', 'starter', 'growth', 'scale'];

  let isAllowed = false;

  if (allowedTiers) {
    // Check if current tier is in allowed list
    isAllowed = allowedTiers.includes(planTier);
  } else if (minTier) {
    // Check if current tier meets minimum
    const currentIndex = tierOrder.indexOf(planTier);
    const minIndex = tierOrder.indexOf(minTier);
    isAllowed = currentIndex >= minIndex;
  }

  if (isAllowed) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (showUpgradePrompt) {
    return (
      <UpgradePrompt
        requiredTier={minTier || allowedTiers?.[0]}
        currentPlan={planTier}
      />
    );
  }

  return null;
};

/**
 * Hook for checking features in code
 */
export const useFeatureGate = (feature) => {
  const { isFeatureEnabled, loading, planTier, getUpgradeUrl } = useBilling();

  return {
    isEnabled: isFeatureEnabled(feature),
    loading,
    planTier,
    upgradeUrl: getUpgradeUrl(),
  };
};

/**
 * Hook for checking limits in code
 */
export const useLimitGate = (limit) => {
  const { canPerformAction, getLimitStatus, loading, planTier, getUpgradeUrl } = useBilling();
  const status = getLimitStatus(limit);

  return {
    canProceed: canPerformAction(limit),
    ...status,
    loading,
    planTier,
    upgradeUrl: getUpgradeUrl(),
  };
};

export default FeatureGate;
